/**
 * THE batch-delete flow: one confirmation sequence, one endpoint, two surfaces.
 *
 * WHY THIS IS A FUNCTION AND NOT A SECOND COPY. The Tactical E2E page has had whole-batch delete
 * since `c57c77c`; the dashboard's batch rows now need the same thing. Duplicating it would
 * duplicate an ESCALATION decision -- specifically the second prompt, which is the only thing
 * standing between an impatient click and a corrupted measurement. Two copies of that is one that
 * gets tightened in one place and stays lax in the other.
 *
 * THE TWO STEPS, and why the second one is not ceremony:
 *
 *  1. Plain confirm, naming what is destroyed. `DELETE /api/batches/{id}` refuses with **409**
 *     while any item is still outstanding.
 *  2. That refusal is shown IN THE SERVER'S OWN WORDS -- it names how many faxes are outstanding
 *     and in which statuses -- and only then is `force=true` offered.
 *
 * An outstanding fax has already been handed to the provider and IS GOING TO ARRIVE. Deleting its
 * row first means inbound correlation finds no candidate and files the arrival as UNMATCHED, which
 * is precisely the signal a burst test exists to measure. So an impatient delete does not merely
 * lose a row: it manufactures the failure the operator is watching for. The server refuses by
 * default and the operator has to opt into that consequence, stated.
 *
 * WHOLE-BATCH ONLY, deliberately. There is no per-item delete: a batch's statistics are meaningful
 * only over the population that was actually sent, so removing one fax would silently shrink the
 * denominator and leave the report describing a 49-fax experiment while claiming to be the 50-fax
 * one that ran.
 *
 * ADMIN-ONLY, enforced BY THE SERVER, not by hiding the button. `auth/middleware.py` treats every
 * `/api/` write method as admin-only and its viewer allow-list contains exactly one entry (a POST
 * to a run's `check-status`, a read-through refresh), so a viewer's DELETE is refused with 403
 * before the handler runs. Callers still gate the button on `isAdmin` -- offering a control that
 * can only fail is its own defect -- but that is presentation, and this function does not depend
 * on it.
 */

/** What is being deleted, in the operator's terms — used verbatim in the first prompt. */
export interface BatchDeleteTarget {
  id: string
  /** e.g. `50 faxes on Nightly eFax` — say what is destroyed, not just that something is. */
  label: string
}

export interface BatchDeleteOutcome {
  /** True when the batch is gone. False means the operator declined at either prompt. */
  deleted: boolean
  /** Set only when the delete was attempted and failed. */
  error?: string
}

export async function confirmAndDeleteBatch(
  target: BatchDeleteTarget,
  deps: {
    confirm?: (message: string) => boolean
    fetchImpl?: typeof fetch
  } = {},
): Promise<BatchDeleteOutcome> {
  // Injectable so the sequence is testable without a browser. Defaults are the real thing, so a
  // caller cannot accidentally get a confirm-free delete by omitting an argument.
  const confirm = deps.confirm ?? ((m: string) => window.confirm(m))
  const doFetch = deps.fetchImpl ?? fetch

  if (
    !confirm(
      `Delete this batch?\n\n${target.label}\n\n` +
        'The batch, every fax in it and their stored received images are removed permanently. ' +
        'This cannot be undone.',
    )
  ) {
    return { deleted: false }
  }

  try {
    let r = await doFetch(`/api/batches/${target.id}`, { method: 'DELETE' })

    if (r.status === 409) {
      // The server states exactly what is outstanding and what deleting anyway will do to the
      // measurement. Show ITS words, not a paraphrase: a paraphrase is where the reason an
      // operator should hesitate goes missing.
      const detail =
        (await r.json().catch(() => ({}))).detail ||
        'Some faxes in this batch are still outstanding.'
      if (!confirm(`${detail}\n\nDelete anyway?`)) return { deleted: false }
      r = await doFetch(`/api/batches/${target.id}?force=true`, { method: 'DELETE' })
    }

    if (!r.ok && r.status !== 204) {
      const detail = (await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`
      throw new Error(typeof detail === 'string' ? detail : `HTTP ${r.status}`)
    }
    return { deleted: true }
  } catch (e) {
    return { deleted: false, error: e instanceof Error ? e.message : String(e) }
  }
}
